import _ from 'lodash'
import Vue from 'vue'
import { useProjectStore, useTrackStore } from '@ve-pro/store'
import {
  SegmentCollection,
  SegmentRatiosCollection,
  RatioTypeUnion,
  SegmentRatios,
  RatioProperties,
} from '@ve-pro/types/track.type'
import { SegmentType } from '@ve-pro/enums/segment'
import { ratioOptions } from '@com/components/ratio-selector/utils'
import { isTemplateSegment } from '../tracks-actions'

export const ratioEffectKeys: Array<keyof RatioProperties> = [
  'x',
  'y',
  'width',
  'height',
  'scale_x',
  'scale_y',
]
/**
 * 元件是否支持多尺寸
 */
export function isSupportRatios(
  segment: SegmentCollection,
): segment is SegmentRatiosCollection {
  const types: SegmentRatiosCollection['type'][] = [
    SegmentType.Video,
    SegmentType.Text,
    SegmentType.Sticker,
  ]

  return types.includes(segment.type as any)
}

// 计算ratio
export function computeRatio(ratio: RatioTypeUnion): number {
  const [width = 0, height = 0] = ratio.split(':').map((item) => Number(item))

  return width / height
}

/**
 * 优先选择相同水平下的ratio相近的布局
 */
export function getNearRatio(
  ratio: RatioTypeUnion,
  ratios: RatioTypeUnion[],
): RatioTypeUnion {
  const rate = computeRatio(ratio)

  return ratios.sort((a, b) => {
    const aRate = computeRatio(a)
    const bRate = computeRatio(b)

    return Math.abs(aRate - rate) - Math.abs(bRate - rate)
  })[0] as RatioTypeUnion
}

/**
 * 指定ratio，填充元件的ratio
 */
export function fillSegmentRatioByRatio(
  segment: SegmentRatiosCollection,
  ratio: RatioTypeUnion,
): void {
  _.forEach(segment.ratios, (val, key) => {
    if (key !== ratio && segment.ratios) {
      Vue.set(
        segment.ratios as SegmentRatios,
        key,
        computeRatioByScale(
          ratio,
          key as RatioTypeUnion,
          segment.ratios[ratio] as RatioProperties,
        ),
      )
    }
  })
}

/**
 * 补充元件相应的ratio
 */
export function fillSegmentRatio(
  segment: SegmentRatiosCollection,
  ratios: RatioTypeUnion[],
): void {
  ratios.forEach((ratio) => {
    if (segment.ratios && !(ratio in segment.ratios)) {
      const nearRatio = getNearRatio(
        ratio,
        Object.keys(segment.ratios) as RatioTypeUnion[],
      )

      Vue.set(
        segment.ratios,
        ratio,
        computeRatioByScale(
          nearRatio,
          ratio,
          segment.ratios[nearRatio] as RatioProperties,
        ),
      )
    }
  })
}

/**
 * 遍历所有元件，填充新增的ratio
 */
export function fillSegmentsRatio(): void {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()
  const segments = trackStore.allSegments

  segments.forEach((segment) => {
    if (isSupportRatios(segment)) {
      fillSegmentRatio(segment, projectStore.ratios)
    }
  })
}

// 通过scale计算出新的ratio数据
function computeRatioByScale(
  ratio: RatioTypeUnion,
  ratio2: RatioTypeUnion,
  ratioData: RatioProperties,
): RatioProperties {
  const newData = _.cloneDeep(ratioData)

  ratioEffectKeys.forEach((k) => {
    if (k in ratioData) {
      const scale = computeRatioScale(ratio, ratio2, k)
      // @ts-ignore
      newData[k] = parseInt(ratioData[k] * scale, 10)
    }
  })

  return newData
}

/**
 * 联动当前尺寸到元件ratios下的其它没有微调过的尺寸
 * @param ratio RatioTypeUnion
 * @param segment SegmentRatiosCollection
 */
export function effectSegmentRatios(
  ratio: RatioTypeUnion,
  segment: SegmentRatiosCollection,
): void {
  if (!segment.ratios) return

  const currentRatio = segment.ratios[ratio]

  if (!currentRatio) return

  // 如果当前尺寸不是联动的尺寸，不做联动
  if (!isSegmentRatioLinkage(ratio)) return

  _.forEach(segment.ratios, (val, key) => {
    // 排除自身、是可以联动的尺寸
    if (val && key !== ratio && isSegmentRatioLinkage(key as RatioTypeUnion)) {
      Vue.set(
        segment.ratios as SegmentRatios,
        key,
        computeRatioByScale(ratio, key as RatioTypeUnion, currentRatio),
      )
    }
  })
}

// 检查ratio是否联动
export function isSegmentRatioLinkage(ratio: RatioTypeUnion): boolean {
  const projectStore = useProjectStore()

  // return !projectStore.configs.unLinkageRatio.includes(ratio)
  return projectStore.configs.isRatioLinkage
}
/**
 * 算出两个尺寸的缩放值
 */
export function computeRatioScale(
  mainRatio: RatioTypeUnion,
  ratio: RatioTypeUnion,
  property: string,
): number {
  // if (property === 'x') {
  //   const mainWidth = getRatioSize(mainRatio)[0] / 2
  //   const width = getRatioSize(ratio)[0] / 2

  //   const scale = width / mainWidth
  //   console.log(
  //     'scale',
  //     scale,
  //     'mainRatio',
  //     mainRatio,
  //     'ratio',
  //     ratio,
  //     'mainWidth',
  //     mainWidth,
  //     'width',
  //     width,
  //   )
  //   return scale
  // }

  return 1
}

// 删除所有元件的ratio属性
export function deleteAllSegmentsRatio(ratio: RatioTypeUnion): void {
  const trackStore = useTrackStore()
  const segments = trackStore.allSegments

  segments.forEach((segment) => {
    removeSegmentRatio(ratio, segment)
  })
}

export function removeSegmentRatio(
  ratio: RatioTypeUnion,
  segment: SegmentCollection,
): void {
  if (isSupportRatios(segment) && segment.ratios && ratio in segment.ratios) {
    delete segment.ratios[ratio]
  }
}

// 删除创意制作元件的ratio属性
export function deleteDefaultSegmentsRatio(ratio: RatioTypeUnion): void {
  const trackStore = useTrackStore()
  const segments = trackStore.allSegments.filter((t) => !isTemplateSegment(t))

  segments.forEach((segment) => {
    removeSegmentRatio(ratio, segment)
  })
}

export function filterRatioOptions(
  ratios: RatioTypeUnion[],
): typeof ratioOptions {
  const _ratioOptions = _.cloneDeep(ratioOptions)

  return _ratioOptions
    .map((option) => {
      return {
        ...option,
        children: option.children.filter((t) => ratios.includes(t.value)),
      }
    })
    .filter((t) => t.children.length)
}
export function containLayout<T extends { width: number; height: number }>(
  data: T,
  target: T,
) {
  const ratio = Math.min(target.width / data.width, target.height / data.height)

  return {
    width: Math.floor(data.width * ratio),
    height: Math.floor(data.height * ratio),
  }
}

/** 更新所有比例的指定属性 */
export function updateRatiosByKey(
  segment: SegmentRatiosCollection,
  data: Partial<RatioProperties>,
): void {
  const ratios = Object.values(segment.ratios ?? {})

  ratios.forEach((ratio) => {
    for (const key in data) {
      ratio[key] = data[key]
    }
  })
}
